import React from 'react'
import styled from 'styled-components'
import {
  Alert,
  Block,
  Button,
  Dialog,
  H4,
  H5,
  Input,
  P,
  Text,
  TextArea
} from '@mote/ui'
import { AdminLayout } from '../../layouts/Admin/admin.layout'
import { EpochToPrettyDate, yesno } from '../../shared/Utils'
import {
  deleteUser,
  freeTrialExtension,
  makeVIP,
  searchUsers,
  unmakeVIP
} from './adminHelpers'

class AdminUsers extends React.Component {
  constructor(props) {
    super(props)
    this.cancelButtonRef = React.createRef(null)

    this.loadingUpdater = (b, err) => {
      if (err) {
        this.setState({ error: err.toString() })
      } else {
        this.setState({ error: undefined })
      }
    }

    this.state = { searchBox: '', showSearchBox: true }
  }

  closeDialog() {
    this.setState({
      deleteConfirmationFor: undefined,
      deleteConfirmation: ''
    })
  }

  render() {
    const searchedUsers = this._getSearchItems()
    const disabled = searchedUsers.length === 0
    return (
      <AdminLayout>
        <Block gap={15} margin='100px 0 0 0'>
          <Block align='center'>
            <H4>Users management</H4>
            {!this.state.showSearchBox && (
              <Button
                variant='link'
                text='(New search)'
                onClick={_ => this.setState({ showSearchBox: true })}
              />
            )}
            {this.state.showSearchBox && (
              <Block width={540} gap={15}>
                <P>
                  Enter some user ids or email addresses below (one per line,
                  space or comma-delimited) to look them up
                </P>

                <TextArea
                  rows={3}
                  value={this.state.searchBox}
                  onChange={this._handleSearchChange.bind(this)}
                  invalid={this.state.error}
                />

                <Button
                  disabled={disabled}
                  onClick={this._handleUserSearch.bind(this)}
                  text='Go!'
                />
              </Block>
            )}

            {this.state.error && (
              <p style={{ color: 'red' }}>{this.state.error}</p>
            )}
            {this.state.confirmationMessage && (
              <p style={{ color: 'blue' }}>{this.state.confirmationMessage}</p>
            )}

            {this.state?.results?.users?.length === 0 && (
              <Alert
                type='info'
                message='No matching users found in our database'
              />
            )}
          </Block>
          {this.state.results?.users?.length > 0 && this._renderUserResults()}

          {this.state.deleteConfirmationFor && (
            <Dialog
              initialFocusRef={this.cancelButtonRef}
              onClose={this.closeDialog.bind(this)}
              isOpen
            >
              <Dialog.Header>Achtung! Beware! ΠΡΟΣΟΧΗ!</Dialog.Header>
              <Dialog.Content>
                <Block gap={15}>
                  <P>
                    This user will lose all their motes if you proceed. This
                    process is{' '}
                    <b>
                      <font color='red'>not reversible</font>
                    </b>
                    . Type the word <b>delete</b> in the box below to confirm
                    deletion.
                  </P>

                  <P>
                    This user has made{' '}
                    {this.state.deleteConfirmationFor.totalMotesMade} mote
                    {this.state.deleteConfirmationFor.totalMotesMade !== 1 &&
                      's'}
                    .
                  </P>
                  <P>
                    <Input
                      value={this.state.deleteConfirmation}
                      type='text'
                      onChange={e =>
                        this.setState({
                          deleteConfirmation: e.target.value
                        })
                      }
                    />
                  </P>
                </Block>
              </Dialog.Content>
              <Dialog.Footer>
                <Button
                  ref={this.cancelButtonRef}
                  variant='secondary'
                  text='Cancel'
                  onClick={this.closeDialog.bind(this)}
                />
                <Button
                  disabled={this.state.deleteConfirmation !== 'delete'}
                  text='Confirm'
                  onClick={() => {
                    this.closeDialog()
                    this._deleteUser(this.state.deleteConfirmationFor)
                  }}
                />
              </Dialog.Footer>
            </Dialog>
          )}
        </Block>
      </AdminLayout>
    )
  }

  _renderUserResults() {
    const users = this.state.results.users

    return (
      <BlockWithOverflow gap={20} margin='40px 0 0 0'>
        <H5>
          Found {users.length} user{users.length > 1 && 's'}
        </H5>

        <Table>
          <thead>
            <tr>
              <th scope='col'>User id</th>
              <th scope='col'>Name</th>
              <th scope='col'>Email</th>
              <th scope='col'>Product</th>
              <th scope='col'>Total motes</th>
              <th scope='col'>Minor?</th>
              <th scope='col'>Paid?</th>
              <th scope='col'>VIP?</th>
              <th scope='col'>On referral Unlimited?</th>
              <th scope='col'>Referral expiry</th>
              <th scope='col'>In free trial?</th>
              <th scope='col'>Institutional?</th>
              <th scope='col'>Admin actions</th>
            </tr>
          </thead>
          <tbody>{users.map(u => this._renderUser(u))}</tbody>
        </Table>
      </BlockWithOverflow>
    )
  }

  _renderUser(u) {
    var showFTE = !(u.isPaid || u.isVIP || u.isMinor || u.institution)
    return (
      <tr key={u.userId}>
        <td>
          <a href={'/account?uid=' + u.userId}>{u.userId}</a>
        </td>
        <td>{u.name}</td>
        <td>{u.primaryEmailAddress}</td>
        <td> {u.productName}</td>
        <td>
          <a href={'/account/activity?uid=' + u.userId}>{u.totalMotesMade}</a>
        </td>
        <td>{yesno(u.isMinor)}</td>
        <td>{yesno(u.isPaid)}</td>
        <td>{yesno(u.isVIP)}</td>
        <td>{yesno(u.isOnReferralUnlimited)}</td>
        <td>
          {u.isOnReferralUnlimited
            ? `Ends at ${EpochToPrettyDate(u.referralUnlimitedExpiryEpoch)}`
            : ''}
        </td>
        {u.isInFreeTrial && (
          <td>
            Ends at {EpochToPrettyDate(u.freeTrialExpiryEpoch)}
            {showFTE && this._showFreeTrialExtension(u)}
          </td>
        )}
        {!u.isInFreeTrial && (
          <td>no {showFTE && this._showFreeTrialExtension(u)} </td>
        )}

        <td>{yesno(u.isInstitution)}</td>
        <td>
          <Block gap={10}>
            {!u.isVIP && (
              <Button
                text='Make VIP'
                variant='link'
                onClick={() => this._makeVIP(u.userId)}
              />
            )}
            {u.isVIP && (
              <Button
                text='Unmake VIP'
                variant='link'
                onClick={() => this._unmakeVIP(u.userId)}
              />
            )}
            {!u.isInstitutionalOwner && (
              <Button
                text='Delete user'
                variant='link'
                onClick={() =>
                  this.setState({
                    deleteConfirmationFor: u.userId
                  })
                }
              />
            )}
          </Block>
        </td>
      </tr>
    )
  }

  _showFreeTrialExtension(u) {
    return (
      <Block margin='10px 0 0 0'>
        <Text tag='small'>Extend by weeks:</Text>

        <Block layout='horizontal' gap={8}>
          {this._extensionButton(u.userId, 2)}
          {this._extensionButton(u.userId, 4)}
          {this._extensionButton(u.userId, 6)}
          {this._extensionButton(u.userId, 8)}
        </Block>
      </Block>
    )
  }

  _extensionButton(u, n) {
    return (
      <Button
        variant='link'
        text={n}
        onClick={e => this._freeTrialExtension(u, n)}
      />
    )
  }

  _getSearchItems(setState, changedValue) {
    const searchBox = changedValue || this.state.searchBox
    if (!searchBox) {
      return []
    }
    const uidsAndEmails = searchBox
      .trim()
      .replace(/\s+/g, ',')
      .replace(/,,/g, ',')
      .split(',')
    if (
      uidsAndEmails.length === 0 ||
      (uidsAndEmails.length === 1 && !uidsAndEmails[0])
    ) {
      return []
    }
    if (setState) {
      this.setState({ error: undefined })
    }
    return uidsAndEmails
  }

  _handleSearchChange(e) {
    this.setState({ searchBox: e.target.value, confirmationMessage: '' })
  }

  _handleUserSearch(e) {
    if (e) {
      e.preventDefault()
    }
    const uidsAndEmails = this._getSearchItems()
    this.loadingUpdater(true)
    searchUsers(
      uidsAndEmails,
      results => {
        this.loadingUpdater(false)
        this.setState({ results, showSearchBox: false })
      },
      err => this.loadingUpdater(false, err)
    )
  }

  _makeVIP(userId) {
    this.loadingUpdater(true)
    makeVIP(
      userId,
      () => {
        this.setState({
          confirmationMessage: `The user [${userId}] is now a VIP`
        })
        this._handleUserSearch()
      },
      err => this.loadingUpdater(false, err)
    )
  }

  _unmakeVIP(userId) {
    this.loadingUpdater(true)
    unmakeVIP(
      userId,
      () => {
        this.setState({
          confirmationMessage: `The user [${userId}] is no longer a VIP`
        })
        this._handleUserSearch()
      },
      err => this.loadingUpdater(false, err)
    )
  }

  _deleteUser(userId) {
    this.loadingUpdater(true)
    deleteUser(
      userId,
      () =>
        this.setState({
          confirmationMessage: `This user [${userId}] will be deleted within a few minutes. You will get a confirmation email.`
        }),
      err => this.loadingUpdater(false, err)
    )
  }

  _freeTrialExtension(userId, weeks) {
    this.loadingUpdater(true)
    freeTrialExtension(
      userId,
      weeks,
      () => {
        this.setState({
          confirmationMessage: `This user [${userId}] has had their trial period extended by ${weeks} weeks`
        })
        this._handleUserSearch()
      },
      err => this.loadingUpdater(false, err)
    )
  }
}

export const AdminUsersScreen = AdminUsers

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 20px;

  td {
    padding: 12px 14px;
    border-top: 1px solid #dee2e6;
    text-align: left;
    line-height: 16px;
    color: #646f7b;
  }

  thead th {
    border: 0;
    vertical-align: bottom;
  }

  tbody tr {
    &:hover {
      background: rgba(213, 212, 218, 0.25);
    }
  }

  th {
    text-align: left;
    padding: 22px 14px;
    font-weight: 400;
    border-top: 1px solid #dee2e6;
    background-color: #f9f9fb;
  }
`

const BlockWithOverflow = styled(Block)`
  overflow-x: auto;
`
